/**
 * หน้าจอใบสั่งซื้อ (Purchase Order, P2-2b) — รายการ ค้นหา ออกใบ และขับ lifecycle
 *
 * ปิดหกในเจ็ด operation ของ contract ที่ `WO-P2-M2-002` เปิดไว้ (D-085):
 * `GET`/`POST /v1/purchase-orders` และสี่ transition
 * (`approve`/`send`/`acknowledge`/`cancel`) ส่วน `GET /{po_id}` ไม่ถูกเรียกเลย เพราะ
 * รายการส่ง **บรรทัดมาพร้อมหัวใบ** อยู่แล้ว และคำตอบของทุกเส้นที่เขียนคือใบฉบับล่าสุด
 *
 * โครงยืมมาจาก `PurchaseRequisitionScreen` ทั้งท่อนโดยตั้งใจ — ช่องค้นหาบนสุด ฟอร์ม
 * สร้างอยู่หลังปุ่มในกล่อง `Modal` ปุ่ม lifecycle รายแถว และหลังสำเร็จก็ **โหลดรายการ
 * ใหม่ผ่าน client เส้นเดิม** ไม่ใช่รีโหลดทั้งหน้า สิ่งที่หน้านี้ต่างออกไปคือ **ฟอร์ม
 * สร้างไม่ได้กรอกว่าจะซื้ออะไร**:
 *
 * 1. **ใบสั่งซื้อเกิดจากการรวมใบขอซื้อ ไม่ใช่การพิมพ์รายการสินค้าใหม่** กล่องสร้างจึง
 *    ดึงใบขอซื้อที่ `APPROVED` มาให้ติ๊กเลือก และสิ่งเดียวที่ผู้ใช้กรอกต่อบรรทัดคือ
 *    **ราคาต่อหน่วย** — สินค้า/จำนวน/หน่วยถูกคัดลอกจากใบขอซื้อโดยเซิร์ฟเวอร์ และ
 *    บรรทัดที่ระบุค่าเหล่านั้นซ้ำถูกปฏิเสธด้วย `400` (D-085)
 * 2. **การออกใบเป็น all-or-nothing** ใบ บรรทัด และการ flip ใบขอซื้อทุกใบเป็น
 *    `CONVERTED_TO_PO` อยู่ในธุรกรรมเดียว ถูกปฏิเสธเมื่อไรก็ไม่มีอะไรถูกเขียนเลย
 * 3. **ผู้ขายที่ถูกระงับออกใบให้ไม่ได้** (D-083) — ใบขอซื้อ *เสนอ* เขาได้เพราะข้อเสนอ
 *    ไม่ผูกมัด ส่วนใบสั่งซื้อคือคำผูกมัด คำอธิบายจึงต้องแยก "ถูกระงับ" ออกจาก
 *    "เลิกใช้ไปแล้ว" ให้ขาด ไม่งั้นผู้ใช้จะไปตามหาปัญหา compliance ที่ไม่มีอยู่จริง
 * 4. **ยกเลิกใบสั่งซื้อไม่คืนใบขอซื้อ** ใบขอซื้อยังอยู่ที่ `CONVERTED_TO_PO` การสั่ง
 *    ใหม่ต้องเปิดใบขอซื้อใบใหม่ (D-085) — หน้าจอพูดข้อนี้ออกมาตรง ๆ ที่ปุ่มยกเลิก
 *
 * การ gate สิทธิ์เป็นแบบเดียวกับทุกหน้าจอธุรกิจ: `PROCUREMENT` + `VIEW` เป็นสิทธิ์ของ
 * **การเปิดหน้าจอนี้** (ด่านอยู่ที่ route) ส่วน `CREATE` (ออกใบ), `EDIT`
 * (ส่ง/ยืนยัน/ยกเลิก) และ `APPROVE` (อนุมัติ) เป็นสิทธิ์ของ **ปุ่มที่เขียน** ซึ่งถูก
 * บังคับข้างในนี้ทีละปุ่ม ตรงกับด่านจริงฝั่งเซิร์ฟเวอร์ทีละเส้น
 *
 * ⚠️ ทั้งหมดนี้เป็น UX layer ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request
 * เสมอ (`references/authorization-rbac.md` ข้อ 5)
 */

import { useCallback, useEffect, useState } from 'react';

import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import { Modal } from '../components/Modal';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import {
  acknowledgePurchaseOrder,
  approvePurchaseOrder,
  cancelPurchaseOrder,
  createPurchaseOrder,
  currentStatusFromFailure,
  fetchPurchaseOrders,
  fetchPurchaseRequisitions,
  ruleFromFailure,
  sendPurchaseOrder,
  setSupplierConfirmedDate,
  type ApiErrorEnvelope,
  type ProcurementFailure,
  type PurchaseOrderTransition,
  type PurchaseOrderWriteResult,
} from '../../lib/procurement/client';
import {
  CONFIRMED_DATE_EDITABLE_STATUSES,
  PURCHASE_ORDER_STATUS_DESCRIPTORS,
  canEditConfirmedDate,
  confirmedDateLabel,
  filterPurchaseOrders,
  orderableRequisitions,
  purchaseOrderActionLabel,
  purchaseOrderActionPermission,
  purchaseOrderActionTargetStatus,
  purchaseOrderLifecycleActionsFor,
  purchaseOrderStatusLabel,
  purchaseOrderStatusTone,
  requiredDateLabel,
  toCreatePurchaseOrderBody,
  type CreatePurchaseOrderFields,
  type PurchaseOrderLineDraft,
} from '../../lib/procurement/procurementView';
import {
  APPROVED_PR_STATUS,
  APPROVER_MUST_DIFFER_FROM_CREATOR,
  PLANT_CODE_MAX_LENGTH,
  PLANT_OUTSIDE_SCOPE,
  PO_CONFIRMED_DATE_NOT_ALLOWED_IN_STATUS,
  PO_NOTE_MAX_LENGTH,
  PO_NUMBER_MAX_LENGTH,
  PO_NUMBER_TAKEN,
  PROCUREMENT_MODULE,
  PURCHASE_ORDER_FIELD_IS_SERVER_OWNED,
  PURCHASE_ORDER_LINE_FIELD_IS_COPIED_FROM_THE_REQUISITION,
  PURCHASE_ORDER_STATUS_CHANGED,
  PURCHASE_ORDER_TRANSITION_NOT_ALLOWED,
  PURCHASE_REQUISITION_ALREADY_ORDERED,
  PURCHASE_REQUISITION_IN_ANOTHER_PLANT,
  PURCHASE_REQUISITION_LISTED_TWICE,
  PURCHASE_REQUISITION_NOT_APPROVED,
  PURCHASE_REQUISITION_NOT_FOUND,
  SUPPLIER_CODE_MAX_LENGTH,
  SUPPLIER_IS_BLOCKED,
  SUPPLIER_IS_INACTIVE,
  SUPPLIER_NOT_FOUND,
  UNIT_PRICE_MUST_NOT_BE_NEGATIVE,
  type PurchaseOrder,
  type PurchaseRequisition,
} from '../../lib/procurement/types';

/* ------------------------------------------------- หน้าจอความล้มเหลว ----- */

type PurchaseOrderFailureContext =
  /** อ่านรายการใบสั่งซื้อ */
  | 'orders'
  /** อ่านใบขอซื้อที่อนุมัติแล้วเพื่อเอามาประกอบใบสั่งซื้อ */
  | 'requisitions'
  /** ออกใบใหม่ */
  | 'create'
  /** กดปุ่ม lifecycle บนแถวใดแถวหนึ่ง */
  | PurchaseOrderTransition
  /** บันทึก/ล้าง "วันส่งที่ Supplier ยืนยัน" ของบรรทัดใดบรรทัดหนึ่ง (D-163 ข้อ 2) */
  | 'confirmed-date';

interface FailureCopy {
  readonly heading: string;
  readonly text: string;
}

/**
 * ประโยค "ไม่มีอะไรเปลี่ยน" ของแต่ละ context ที่ **เขียน** — สตริงว่างสำหรับ context
 * ที่อ่านอย่างเดียว
 *
 * ของ `create` ยาวกว่าที่อื่นเพราะมันต้องพูดถึง **ใบขอซื้อ** ด้วย: ผู้ใช้ที่ถูกปฏิเสธ
 * กลางทางจะสงสัยว่าใบที่ตัวเองเลือกไปถูกกินไปแล้วหรือยัง คำตอบคือยัง — ทั้งชุดอยู่ใน
 * ธุรกรรมเดียว (D-085) และนี่เป็นข้อเท็จจริงของ contract ไม่ใช่คำปลอบใจ
 */
function nothingChangedText(context: PurchaseOrderFailureContext): string {
  switch (context) {
    case 'create':
      return ' ไม่มีใบสั่งซื้อใบใดถูกเปิด ไม่มีใบขอซื้อใบใดถูกเปลี่ยนสถานะ และไม่มีแถว audit ถูกเขียน';
    case 'approve':
    case 'send':
    case 'acknowledge':
    case 'cancel':
      return ' ใบนี้ยังอยู่ในสถานะเดิมและไม่มีแถว audit ถูกเขียน';
    case 'confirmed-date':
      return ' วันที่ของบรรทัดนี้ยังเป็นค่าเดิม และสิ่งที่พิมพ์ไว้ในช่องยังคงอยู่ให้แก้แล้วส่งใหม่ได้';
    case 'orders':
    case 'requisitions':
      return '';
  }
}

function isWriteContext(context: PurchaseOrderFailureContext): boolean {
  return nothingChangedText(context) !== '';
}

/**
 * ข้อความของกฎแต่ละข้อ — คืน `null` เมื่อไม่ใช่กฎที่ contract เขียนไว้
 *
 * ⚠️ ห้ามเพิ่มรหัสที่ contract ไม่ได้ระบุลงใน `switch` นี้ — รหัสที่หน้าจอคิดขึ้นเอง
 * คือข้อความที่ไม่มีวันแสดง และเป็นคำสัญญาที่ backend ไม่เคยให้ไว้
 */
function ruleCopy(rule: string | null, currentStatus: string | null): FailureCopy | null {
  switch (rule) {
    case PO_NUMBER_TAKEN:
      return {
        heading: 'เลขใบสั่งซื้อนี้มีเจ้าของอยู่แล้ว',
        text: `เซิร์ฟเวอร์ตอบ 409 พร้อมกฎ ${PO_NUMBER_TAKEN} — เลขใบสั่งซื้อต้องไม่ซ้ำกันทั้งระบบและแก้ไม่ได้หลังออกใบ (D-085) ใช้เลขอื่นแล้วส่งใหม่ได้ทันที **ใบที่ถือเลขนี้อยู่ไม่ถูกทับ และใบขอซื้อที่เลือกไว้ยังเลือกได้อยู่**`,
      };
    case PLANT_OUTSIDE_SCOPE:
      return {
        heading: 'โรงงานที่ระบุอยู่นอกขอบเขตของคุณ',
        text: `เซิร์ฟเวอร์ตอบ 403 พร้อมกฎ ${PLANT_OUTSIDE_SCOPE} — ใบสั่งซื้อเป็นของโรงงานเดียว และคุณออกใบให้โรงงานที่ไม่ได้อยู่ในสิทธิ์เข้าถึงของตัวเองไม่ได้ (D-004) ตรวจรหัสโรงงาน หรือขอสิทธิ์เข้าถึงโรงงานนั้นเพิ่ม`,
      };
    case SUPPLIER_NOT_FOUND:
      return {
        heading: 'ไม่พบผู้ขายรหัสนี้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${SUPPLIER_NOT_FOUND} — ไม่มีผู้ขายรหัสนี้ในทะเบียน ตรวจรหัสอีกครั้งจากหน้า "ผู้ขาย (Supplier Master)"`,
      };
    case SUPPLIER_IS_BLOCKED:
      return {
        heading: 'ผู้ขายรายนี้ถูกระงับ — ออกใบสั่งซื้อให้ไม่ได้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${SUPPLIER_IS_BLOCKED} — ผู้ขายที่ถูกระงับ (BLOCKED) รับคำสั่งซื้อใหม่ไม่ได้ (D-083) **ใบขอซื้อยัง "เสนอ" เขาได้เพราะข้อเสนอไม่ผูกมัดอะไร แต่ใบสั่งซื้อคือคำผูกมัด** ถ้าจำเป็นต้องซื้อจากรายนี้จริง ต้องให้ผู้ดูแลทะเบียนผู้ขายปลดสถานะระงับก่อน — ไม่ใช่เรื่องสิทธิ์ของคุณ ไม่มีใบสั่งซื้อถูกเปิดและไม่มีใบขอซื้อใบใดถูกเปลี่ยนสถานะ`,
      };
    case SUPPLIER_IS_INACTIVE:
      return {
        heading: 'ผู้ขายรายนี้ถูกเลิกใช้ไปแล้ว',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${SUPPLIER_IS_INACTIVE} — **คนละเรื่องกับการถูกระงับ**: รายนี้ไม่ได้มีปัญหาด้าน compliance แต่เป็นความสัมพันธ์ที่ปิดไปแล้ว (INACTIVE) ใบสั่งซื้อออกให้ได้เฉพาะผู้ขายที่ ACTIVE เท่านั้น ไม่มีใบสั่งซื้อถูกเปิดและไม่มีใบขอซื้อใบใดถูกเปลี่ยนสถานะ`,
      };
    case PURCHASE_REQUISITION_NOT_FOUND:
      return {
        heading: 'ไม่พบใบขอซื้อที่อ้างถึงในบรรทัด',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${PURCHASE_REQUISITION_NOT_FOUND} — เลขใบขอซื้อในบรรทัดไม่มีอยู่จริง โหลดกล่องนี้ใหม่เพื่อดูใบที่สั่งซื้อได้จริงในขณะนี้`,
      };
    case PURCHASE_REQUISITION_IN_ANOTHER_PLANT:
      return {
        heading: 'ใบขอซื้อในบรรทัดเป็นของโรงงานอื่น',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${PURCHASE_REQUISITION_IN_ANOTHER_PLANT} — ความต้องการที่เปิดไว้ที่โรงงานหนึ่งสั่งซื้อบนเอกสารของอีกโรงงานไม่ได้ (D-004) ตรวจรหัสโรงงานของใบสั่งซื้อให้ตรงกับโรงงานของใบขอซื้อที่เลือกไว้`,
      };
    case PURCHASE_REQUISITION_NOT_APPROVED:
      return {
        heading: 'ใบขอซื้อในบรรทัดไม่ได้อยู่ที่ "อนุมัติแล้ว"',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${PURCHASE_REQUISITION_NOT_APPROVED} — สั่งซื้อได้เฉพาะใบขอซื้อที่ APPROVED เท่านั้น${
          currentStatus === null ? '' : ` ขณะนี้ใบนั้นอยู่ที่ ${currentStatus}`
        } กรณีที่พบบ่อยที่สุดคือมีคนออกใบสั่งซื้อให้ไปก่อนแล้ว (ใบขอซื้อถูกเปลี่ยนเป็น CONVERTED_TO_PO) — โหลดกล่องนี้ใหม่เพื่อดูใบที่ยังสั่งได้`,
      };
    case PURCHASE_REQUISITION_LISTED_TWICE:
      return {
        heading: 'ใบขอซื้อใบเดียวถูกใส่ลงในใบสั่งซื้อนี้สองครั้ง',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${PURCHASE_REQUISITION_LISTED_TWICE} — ใบขอซื้อหนึ่งใบมีได้หนึ่งบรรทัดเท่านั้น ถ้าต้องการซื้อเพิ่ม ต้องเปิดใบขอซื้อใบใหม่`,
      };
    case PURCHASE_REQUISITION_ALREADY_ORDERED:
      return {
        heading: 'ใบขอซื้อในบรรทัดอยู่บนใบสั่งซื้อใบอื่นแล้ว',
        text: `เซิร์ฟเวอร์ตอบ 409 พร้อมกฎ ${PURCHASE_REQUISITION_ALREADY_ORDERED} — ความต้องการหนึ่งใบถูกสั่งซื้อได้ครั้งเดียว (ฐานข้อมูลกันไว้ด้วย unique constraint) การสั่งซ้ำคือการซื้อของสองรอบสำหรับความต้องการเดียว — ถ้าต้องการของเพิ่มจริง ต้องเปิดใบขอซื้อใบใหม่`,
      };
    case PURCHASE_ORDER_LINE_FIELD_IS_COPIED_FROM_THE_REQUISITION:
      return {
        heading: 'หน้าจอกับเซิร์ฟเวอร์ไม่ตรงกัน — บรรทัดส่งสิ่งที่คัดลอกมาจากใบขอซื้อไปด้วย',
        text: `เซิร์ฟเวอร์ตอบ 400 พร้อมกฎ ${PURCHASE_ORDER_LINE_FIELD_IS_COPIED_FROM_THE_REQUISITION} — สินค้า จำนวน และหน่วยนับเป็นของใบขอซื้อ บรรทัดของใบสั่งซื้อส่งได้แค่เลขใบขอซื้อกับราคาต่อหน่วย ไม่มีช่องใดในกล่องนี้กรอกมันได้ การที่มันไปถึงเซิร์ฟเวอร์จึงเป็นบั๊ก — แจ้งผู้ดูแลระบบ`,
      };
    case PURCHASE_ORDER_FIELD_IS_SERVER_OWNED:
      return {
        heading: 'หน้าจอกับเซิร์ฟเวอร์ไม่ตรงกัน — ฟิลด์ของเซิร์ฟเวอร์ถูกส่งไปด้วย',
        text: `เซิร์ฟเวอร์ตอบ 400 พร้อมกฎ ${PURCHASE_ORDER_FIELD_IS_SERVER_OWNED} — สถานะ ผู้ออกใบ ผู้อนุมัติ และเวลาทั้งหมดเป็นของเซิร์ฟเวอร์ ไม่มีฟอร์มใดในหน้าจอนี้กรอกมันได้ การที่มันไปถึงเซิร์ฟเวอร์จึงเป็นบั๊ก — แจ้งผู้ดูแลระบบ`,
      };
    case UNIT_PRICE_MUST_NOT_BE_NEGATIVE:
      return {
        heading: 'ราคาต่อหน่วยติดลบไม่ได้',
        text: `เซิร์ฟเวอร์ตอบ 400 พร้อมกฎ ${UNIT_PRICE_MUST_NOT_BE_NEGATIVE} — ราคาศูนย์ใช้ได้ (ของแถมเป็นรายการที่มีอยู่จริงบนใบสั่งซื้อ) แต่ค่าติดลบไม่ใช่ราคา`,
      };
    case PURCHASE_ORDER_STATUS_CHANGED:
      return {
        heading: 'มีคนเปลี่ยนสถานะใบนี้ไปก่อนแล้ว',
        text: `เซิร์ฟเวอร์ตอบ 409 พร้อมกฎ ${PURCHASE_ORDER_STATUS_CHANGED} — สถานะที่หน้าจอนี้อ่านมาตอนโหลดไม่ตรงกับที่เก็บอยู่จริง${
          currentStatus === null
            ? ''
            : ` ขณะนี้ใบนี้อยู่ที่ ${purchaseOrderStatusLabel(currentStatus)}`
        } โหลดรายการใหม่เพื่อดูสถานะจริงก่อนตัดสินใจ — **การกดซ้ำจะได้คำตอบเดิม**`,
      };
    case PURCHASE_ORDER_TRANSITION_NOT_ALLOWED:
      return {
        heading: 'สถานะปัจจุบันของใบนี้ไม่มีเส้นทางไปยังสิ่งที่กด',
        text: `เซิร์ฟเวอร์ตอบ 409 พร้อมกฎ ${PURCHASE_ORDER_TRANSITION_NOT_ALLOWED} —${
          currentStatus === null
            ? ''
            : ` ใบนี้อยู่ที่ ${purchaseOrderStatusLabel(currentStatus)} และ`
        } lifecycle ไม่มีเส้นทางนี้ ใบที่งานรับเข้าขยับไปแล้ว (รับเข้าบางส่วน/ปิดใบ) ยกเลิกไม่ได้ — ของมาถึงแล้วจริง ๆ`,
      };
    case APPROVER_MUST_DIFFER_FROM_CREATOR:
      return {
        heading: 'ผู้อนุมัติต้องไม่ใช่ผู้ออกใบ',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${APPROVER_MUST_DIFFER_FROM_CREATOR} — **ไม่ใช่เรื่องสิทธิ์ไม่พอ** คุณถือ ${PROCUREMENT_MODULE} + APPROVE ครบแล้ว แต่ใบนี้คุณเป็นผู้ออกเอง การแบ่งแยกหน้าที่ (D-010) จึงให้คนอื่นเป็นผู้อนุมัติ — ขอสิทธิ์เพิ่มไม่ช่วยอะไร ต้องให้เพื่อนร่วมงานอีกคนกดอนุมัติ **ใบนี้ยังอยู่ที่สถานะเดิม**`,
      };
    case PO_CONFIRMED_DATE_NOT_ALLOWED_IN_STATUS:
      return {
        heading: 'บันทึก "วันส่งที่ Supplier ยืนยัน" ไม่ได้ในสถานะนี้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${PO_CONFIRMED_DATE_NOT_ALLOWED_IN_STATUS} — กรอกวันที่นี้ได้เฉพาะใบที่อยู่ที่ ${CONFIRMED_DATE_EDITABLE_STATUSES.map(
          (status) => purchaseOrderStatusLabel(status),
        ).join(', ')} เท่านั้น${
          currentStatus === null
            ? ''
            : ` ขณะนี้ใบนี้อยู่ที่ ${purchaseOrderStatusLabel(currentStatus)}`
        } โหลดรายการใหม่เพื่อดูสถานะจริงก่อนลองอีกครั้ง`,
      };
    default:
      return null;
  }
}

/**
 * ข้อความประจำแต่ละสถานะ — แจกแจงครบโดยไม่มี `default` เพื่อให้ TypeScript เป็นผู้
 * พิสูจน์ว่าไม่มีสถานะไหนตกหล่นเมื่อ client เพิ่มกรณีใหม่
 */
function failureCopy(
  failure: ProcurementFailure,
  context: PurchaseOrderFailureContext,
): FailureCopy {
  const writing = isWriteContext(context);
  const nothingChanged = nothingChangedText(context);
  const currentStatus = currentStatusFromFailure(failure);

  switch (failure.status) {
    case 'unauthenticated':
      return {
        heading: 'ต้องเข้าสู่ระบบก่อนจึงจะใช้หน้าจอนี้ได้',
        text: 'เซิร์ฟเวอร์ตอบ 401 — เซสชันหมดอายุหรือถูกยกเลิกไปแล้ว token ที่ถืออยู่จึงถูกล้างทิ้งและต้องเข้าสู่ระบบใหม่ (D-049)',
      };
    case 'forbidden':
      return (
        ruleCopy(failure.error.rule, currentStatus) ?? {
          heading: 'สิทธิ์ชุดนี้ไม่ครอบคลุมคำขอนี้',
          text: `เซิร์ฟเวอร์ตอบ 403 — คำขอนี้ต้องการโมดูล ${PROCUREMENT_MODULE} ในระดับที่สูงกว่าที่แผนกของคุณถืออยู่ ขอสิทธิ์เพิ่มที่ตารางสิทธิ์ตามแผนก${nothingChanged}`,
        }
      );
    case 'not-found':
      return {
        heading: 'ไม่พบใบสั่งซื้อที่คำขอนี้อ้างถึง',
        text: `เซิร์ฟเวอร์ตอบ 404 — ใบนี้ไม่มีอยู่แล้ว หรืออยู่ในโรงงานนอกขอบเขตของคุณ (สองกรณีนี้ตอบเหมือนกันโดยตั้งใจ ไม่งั้นคนที่ไซต์หนึ่งจะไล่เดาได้ว่าอีกไซต์ซื้ออะไรอยู่) โหลดรายการใหม่แล้วลองอีกครั้ง${nothingChanged}`,
      };
    case 'conflict':
      return (
        ruleCopy(failure.error.rule, currentStatus) ?? {
          heading: 'มีของที่ชนกันอยู่แล้วในระบบ',
          text: `เซิร์ฟเวอร์ตอบ 409 — สิ่งที่ส่งไปชนกับสถานะที่เก็บอยู่จริง ไม่ใช่ข้อผิดพลาดของระบบและการกดซ้ำจะได้คำตอบเดิม${nothingChanged}`,
        }
      );
    case 'rule-violation':
      return (
        ruleCopy(failure.error.rule, currentStatus) ?? {
          heading: 'คำขอนี้ติดกฎทางธุรกิจ',
          text: `เซิร์ฟเวอร์ตอบ 422 พร้อมรหัสกฎที่ชน — ไม่ใช่ข้อผิดพลาดของระบบ แต่เป็นเงื่อนไขที่ต้องแก้ก่อน${nothingChanged}`,
        }
      );
    case 'bad-request':
      return (
        ruleCopy(failure.error.rule, currentStatus) ?? {
          heading: 'คำขอนี้ถูกปฏิเสธว่าไม่ถูกต้อง',
          text: `เซิร์ฟเวอร์ตอบ 400 — ข้อมูลที่ส่งไปไม่ตรงรูปแบบที่ API รับ (เช่น เลขใบยาวเกิน ${PO_NUMBER_MAX_LENGTH} ตัวอักษร หรือหมายเหตุยาวเกิน ${PO_NOTE_MAX_LENGTH} ตัวอักษร)${nothingChanged}`,
        }
      );
    case 'server-error':
      return {
        heading: `เซิร์ฟเวอร์ตอบ ${failure.httpStatus}`,
        text: writing
          ? 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ และ **ไม่มีอะไรยืนยันว่าข้อมูลถูกเปลี่ยนหรือไม่** — โหลดรายการใหม่เพื่อดูสถานะที่เก็บอยู่จริงก่อนลองอีกครั้ง'
          : 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ ยังไม่มีข้อมูลจริงมาแสดง',
      };
    case 'network-error':
      return {
        heading: 'ติดต่อเซิร์ฟเวอร์ไม่ได้',
        text: writing
          ? `${failure.detail} — ไม่มีอะไรยืนยันว่าคำขอไปถึงเซิร์ฟเวอร์หรือไม่ โหลดรายการใหม่เพื่อดูสถานะที่เก็บอยู่จริงก่อนลองอีกครั้ง`
          : `${failure.detail} — หน้าจอนี้จึงไม่มีข้อมูลจะแสดง และจะไม่เดาขึ้นมาเอง`,
      };
    case 'invalid-response':
      return {
        heading: 'ข้อมูลจากเซิร์ฟเวอร์ผิดรูปแบบ',
        text: 'หน้าจอปฏิเสธที่จะแสดงเอกสารที่อ่านได้ไม่ครบ — ใบสั่งซื้อที่ขาดไปหนึ่งบรรทัดคือคำสั่งซื้อที่หน้าจอบอกว่าซื้อน้อยกว่าที่ผู้ขายได้รับจริง',
      };
    case 'cancelled':
      return {
        heading: 'ยกเลิกการเรียกข้อมูลแล้ว',
        text: 'คำขอถูกยกเลิกก่อนได้รับคำตอบ',
      };
  }
}

function envelopeOf(failure: ProcurementFailure): ApiErrorEnvelope | null {
  return 'error' in failure ? failure.error : null;
}

export interface PurchaseOrderFailureNoticeProps {
  readonly failure: ProcurementFailure;
  readonly context: PurchaseOrderFailureContext;
  readonly onRetry?: () => void;
}

export function PurchaseOrderFailureNotice({
  failure,
  context,
  onRetry,
}: PurchaseOrderFailureNoticeProps) {
  const copy = failureCopy(failure, context);
  const envelope = envelopeOf(failure);
  const rule = ruleFromFailure(failure);
  const serverMessage = envelope === null ? null : envelope.message;
  // `409` และ `422` เป็นคำตอบที่ **ผู้ใช้จัดการต่อเองได้** (ใช้เลขอื่น เลือกใบอื่น
  // โหลดใหม่ หรือให้เพื่อนร่วมงานกดอนุมัติ) ต่างจากความล้มเหลวที่เหลือซึ่งเขาทำอะไร
  // ไม่ได้เลย
  const refusedByRule =
    failure.status === 'rule-violation' || failure.status === 'conflict';

  return (
    <div
      className="cap-notice"
      data-testid="po-failure"
      data-failure={failure.status}
      data-context={context}
      data-rule={rule ?? ''}
      role="status"
    >
      <StatusBadge tone={refusedByRule ? 'warning' : 'danger'}>
        {refusedByRule ? 'ติดกฎทางธุรกิจ' : 'ทำรายการไม่สำเร็จ'}
      </StatusBadge>
      <h3 className="cap-notice__title">{copy.heading}</h3>
      <p className="cap-notice__text">{copy.text}</p>

      {rule === null ? null : (
        <p className="cap-notice__text">
          กฎที่ชน:{' '}
          <code className="cap-table__code" data-testid="po-rule">
            {rule}
          </code>
        </p>
      )}

      {serverMessage === null ? null : (
        <p className="cap-notice__note" data-testid="po-failure-message">
          ข้อความจากเซิร์ฟเวอร์: {serverMessage}
        </p>
      )}

      {onRetry === undefined ? null : (
        <div className="cap-notice__actions">
          <button type="button" className="cap-button cap-button--ghost" onClick={onRetry}>
            ลองอีกครั้ง
          </button>
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- หน้าจอ -- */

export interface PurchaseOrderScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  /** inject ได้เพื่อการทดสอบ — production ใช้ `fetch` ของเบราว์เซอร์ */
  readonly fetchImpl?: typeof fetch;
}

type OrdersState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly PurchaseOrder[] }
  | ProcurementFailure;

/** ใบขอซื้อที่สั่งซื้อได้ ซึ่งกล่องสร้างโหลดมาให้เลือก */
type OrderableState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly PurchaseRequisition[] }
  | ProcurementFailure;

/** คำขอที่กำลังเดินทางอยู่ — ใช้ปิดปุ่มทั้งหน้าจอระหว่างนั้น */
type Pending =
  | { readonly kind: 'create' }
  | { readonly kind: PurchaseOrderTransition; readonly poId: string }
  | { readonly kind: 'confirmed-date'; readonly poId: string; readonly lineNo: number };

/** ผลของปุ่ม lifecycle ที่ผูกกับแถวที่กด — ทั้งสำเร็จและไม่สำเร็จอยู่ที่แถวนั้น */
interface ActionOutcome {
  readonly poId: string;
  readonly transition: PurchaseOrderTransition;
  readonly failure: ProcurementFailure | null;
  readonly order: PurchaseOrder | null;
}

/** ผลของการบันทึก/ล้างวันที่ ผูกกับ**บรรทัดที่กด** ไม่ใช่กับทั้งใบ (D-163 ข้อ 2) */
interface ConfirmedDateOutcome {
  readonly poId: string;
  readonly lineNo: number;
  readonly failure: ProcurementFailure | null;
}

/** คีย์ของ draft/outcome ต่อบรรทัด — `po_id` คู่กับ `line_no` เพราะ `line_no` ไม่ unique ข้ามใบ */
function confirmedDateKey(poId: string, lineNo: number): string {
  return `${poId}:${lineNo}`;
}

const EMPTY_CREATE_FIELDS: CreatePurchaseOrderFields = {
  poNumber: '',
  plantCode: '',
  supplierCode: '',
  note: '',
};

/**
 * ราคาต่อบรรทัดที่ผู้ใช้กรอก — **คีย์คือ `pr_number` ของใบที่ถูกติ๊กเลือก**
 *
 * คีย์ที่ไม่มีอยู่ = ใบนั้นไม่ถูกเลือก ส่วนคีย์ที่มีค่าเป็นสตริงว่าง = เลือกแล้วแต่ยัง
 * ไม่ได้ใส่ราคา (ปุ่มบันทึกยังกดไม่ได้) — สองสถานะนี้ต้องแยกกัน ไม่งั้นการติ๊กเลือก
 * จะเงียบหายไปเมื่อผู้ใช้ลบราคาที่พิมพ์ไว้ออก
 */
type LinePrices = Readonly<Record<string, string>>;

export function PurchaseOrderScreen({
  permissions,
  apiBaseUrl,
  fetchImpl,
}: PurchaseOrderScreenProps) {
  const canCreate = can(permissions, PROCUREMENT_MODULE, 'CREATE');
  const canEdit = can(permissions, PROCUREMENT_MODULE, 'EDIT');
  const canApprove = can(permissions, PROCUREMENT_MODULE, 'APPROVE');

  const [orders, setOrders] = useState<OrdersState>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const [pending, setPending] = useState<Pending | null>(null);

  const [query, setQuery] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [createFields, setCreateFields] =
    useState<CreatePurchaseOrderFields>(EMPTY_CREATE_FIELDS);
  const [linePrices, setLinePrices] = useState<LinePrices>({});
  const [orderable, setOrderable] = useState<OrderableState>({ status: 'loading' });
  const [orderableToken, setOrderableToken] = useState(0);
  const [createFailure, setCreateFailure] = useState<ProcurementFailure | null>(null);
  const [createdOrder, setCreatedOrder] = useState<PurchaseOrder | null>(null);

  const [actionOutcome, setActionOutcome] = useState<ActionOutcome | null>(null);

  /** ค่าในช่อง `<input type="date">` ของแต่ละบรรทัดที่ผู้ใช้กำลังแก้ ยังไม่ส่ง */
  const [confirmedDateDrafts, setConfirmedDateDrafts] = useState<
    Readonly<Record<string, string>>
  >({});
  const [confirmedDateOutcome, setConfirmedDateOutcome] =
    useState<ConfirmedDateOutcome | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    // ระหว่างโหลดซ้ำหลังกดปุ่มสำเร็จ **ตารางเดิมยังอยู่** ไม่กระพริบเป็นหน้าว่าง
    setOrders((previous) => (previous.status === 'ready' ? previous : { status: 'loading' }));

    const options = { apiBaseUrl, fetchImpl, signal: controller.signal };

    void fetchPurchaseOrders({}, options).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setOrders(
        result.status === 'ready' ? { status: 'ready', items: result.page.items } : result,
      );
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, fetchImpl, reloadToken]);

  /**
   * ใบขอซื้อที่สั่งซื้อได้ — โหลด **ตอนเปิดกล่อง** ไม่ใช่ตอนเปิดหน้าจอ
   *
   * รายการนี้เปลี่ยนเร็วกว่ารายการใบสั่งซื้อ (เพื่อนร่วมงานอนุมัติใบใหม่ หรือออกใบ
   * สั่งซื้อกินใบไปก่อน) และมันมีความหมายเฉพาะตอนที่ผู้ใช้กำลังจะออกใบเท่านั้น การ
   * โหลดตอนเปิดกล่องจึงให้ภาพที่สดที่สุด ณ เวลาที่มันถูกใช้จริง
   *
   * เซิร์ฟเวอร์กรองด้วย `status=APPROVED` ให้แล้ว และ `orderableRequisitions()` เป็น
   * ด่านที่สองฝั่งหน้าจอ
   */
  useEffect(() => {
    if (!createOpen) {
      return;
    }

    const controller = new AbortController();
    let active = true;
    setOrderable({ status: 'loading' });

    const options = { apiBaseUrl, fetchImpl, signal: controller.signal };

    void fetchPurchaseRequisitions({ status: APPROVED_PR_STATUS }, options).then(
      (result) => {
        if (!active || result.status === 'cancelled') {
          return;
        }
        setOrderable(
          result.status === 'ready'
            ? { status: 'ready', items: orderableRequisitions(result.page.items) }
            : result,
        );
      },
    );

    return () => {
      active = false;
      controller.abort();
    };
  }, [createOpen, orderableToken, apiBaseUrl, fetchImpl]);

  const openCreate = () => {
    if (!canCreate) {
      return;
    }
    // เปิดกล่องใหม่ = เริ่มใหม่: ผลของครั้งก่อน (สำเร็จหรือถูกปฏิเสธ) ไม่ใช่ผลของครั้งนี้
    setCreateFields(EMPTY_CREATE_FIELDS);
    setLinePrices({});
    setCreateFailure(null);
    setCreatedOrder(null);
    setCreateOpen(true);
  };

  /** อ้างอิงคงที่ — `Modal` ผูก/ถอน listener ของ Esc ตาม `onClose` */
  const closeCreate = useCallback(() => {
    setCreateOpen(false);
  }, []);

  const updateField = (field: keyof CreatePurchaseOrderFields, value: string) => {
    setCreateFields((previous) => ({ ...previous, [field]: value }));
  };

  /** ติ๊ก/เอาติ๊กออก — การเอาออกลบราคาที่กรอกไว้ของใบนั้นทิ้งไปด้วย */
  const togglePick = (prNumber: string) => {
    setLinePrices((previous) => {
      if (!(prNumber in previous)) {
        return { ...previous, [prNumber]: '' };
      }
      return Object.fromEntries(
        Object.entries(previous).filter(([key]) => key !== prNumber),
      );
    });
  };

  const setPrice = (prNumber: string, value: string) => {
    setLinePrices((previous) =>
      prNumber in previous ? { ...previous, [prNumber]: value } : previous,
    );
  };

  const orderableItems = orderable.status === 'ready' ? orderable.items : [];

  /**
   * บรรทัดที่จะเดินทางออกไป — ประกอบจาก **รายการที่เซิร์ฟเวอร์ส่งมา** ไม่ใช่จากคีย์ใน
   * `linePrices` ตรง ๆ
   *
   * สองผลที่ต้องการจากลำดับนี้: บรรทัดเรียงตามที่ผู้ใช้เห็นบนจอ และใบที่ถูกติ๊กไว้แล้ว
   * **หายไปจากรายการหลังโหลดใหม่** (เช่นมีคนออกใบสั่งซื้อกินไปก่อน) จะไม่ติดไปกับคำขอ
   * โดยที่ไม่มีใครเห็น
   */
  const lines: readonly PurchaseOrderLineDraft[] = orderableItems
    .filter((requisition) => requisition.pr_number in linePrices)
    .map((requisition) => ({
      prNumber: requisition.pr_number,
      unitPrice: linePrices[requisition.pr_number],
    }));

  const submitCreate = async () => {
    const body = toCreatePurchaseOrderBody(createFields, lines);
    if (body === null || pending !== null || !canCreate) {
      return;
    }

    setPending({ kind: 'create' });
    setCreateFailure(null);
    setCreatedOrder(null);

    const result = await createPurchaseOrder(body, { apiBaseUrl, fetchImpl });
    setPending(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      // **ไม่แตะรายการเลย และไม่ปิดกล่อง** — สิ่งที่กรอกและติ๊กไว้ยังอยู่ให้แก้แล้วส่ง
      // ใหม่ได้ทันที ซึ่งสำหรับ `PO_NUMBER_TAKEN` คือสิ่งเดียวที่ต้องทำ
      setCreateFailure(result);
      return;
    }

    setCreatedOrder(result.order);
    setCreateOpen(false);
    // คำค้นที่ค้างอยู่ถูกล้างทิ้ง ไม่งั้นใบที่เพิ่งออก (ซึ่งมักไม่ตรงกับคำค้นเดิม) จะถูก
    // กรองออกทันทีที่โหลดเสร็จ
    setQuery('');
    setReloadToken((token) => token + 1);
  };

  const runTransition = async (
    order: PurchaseOrder,
    transition: PurchaseOrderTransition,
  ) => {
    if (
      pending !== null ||
      !can(permissions, PROCUREMENT_MODULE, purchaseOrderActionPermission(transition))
    ) {
      return;
    }

    setPending({ kind: transition, poId: order.po_id });
    setActionOutcome(null);

    // `expected_status` คือสถานะที่ผู้กด **เห็นอยู่บนจอ** ตอนกด — ด่านนี้คือสิ่งเดียว
    // ที่แยก "มีคนกดไปก่อนแล้ว" ออกจาก "เส้นทางนี้ไม่มีอยู่จริง" ได้
    const body = { expected_status: order.status };
    const options = { apiBaseUrl, fetchImpl };

    let result: PurchaseOrderWriteResult;
    switch (transition) {
      case 'approve':
        result = await approvePurchaseOrder(order.po_id, body, options);
        break;
      case 'send':
        result = await sendPurchaseOrder(order.po_id, body, options);
        break;
      case 'acknowledge':
        result = await acknowledgePurchaseOrder(order.po_id, body, options);
        break;
      case 'cancel':
        result = await cancelPurchaseOrder(order.po_id, body, options);
        break;
    }

    setPending(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      // **ไม่แตะรายการเลย** — คำอธิบายอยู่ที่แถวที่เขากด ไม่ใช่ลอยอยู่ท้ายหน้า
      setActionOutcome({
        poId: order.po_id,
        transition,
        failure: result,
        order: null,
      });
      return;
    }

    setActionOutcome({
      poId: order.po_id,
      transition,
      failure: null,
      order: result.order,
    });
    // สถานะใหม่มาจาก **การโหลดรายการใหม่** ไม่ใช่การแก้แถวในหน่วยความจำ
    setReloadToken((token) => token + 1);
  };

  /** พิมพ์ในช่องวันที่ของบรรทัดนั้น — เลิกแสดงผลครั้งก่อน (สำเร็จ/ล้มเหลว) ของบรรทัดนี้ */
  const updateConfirmedDateDraft = (poId: string, lineNo: number, value: string) => {
    const key = confirmedDateKey(poId, lineNo);
    setConfirmedDateDrafts((previous) => ({ ...previous, [key]: value }));
    setConfirmedDateOutcome((previous) =>
      previous !== null && previous.poId === poId && previous.lineNo === lineNo
        ? null
        : previous,
    );
  };

  /**
   * บันทึก/ล้างวันส่งที่ Supplier ยืนยันของบรรทัดเดียว (D-163 ข้อ 2)
   *
   * สำเร็จ → **แก้แถวของใบนี้ในหน่วยความจำด้วยคำตอบของเซิร์ฟเวอร์** ไม่ใช่โหลด
   * รายการทั้งหน้าใหม่ (ต่างจาก transition อื่นโดยตั้งใจ — นี่คือสิ่งเดียวที่ WO
   * ฉบับนี้ระบุต่างจากปุ่ม lifecycle เดิม)
   */
  const submitConfirmedDate = async (
    order: PurchaseOrder,
    lineNo: number,
    date: string | null,
  ) => {
    if (pending !== null || !canEditConfirmedDate(order.status, permissions)) {
      return;
    }

    setPending({ kind: 'confirmed-date', poId: order.po_id, lineNo });
    setConfirmedDateOutcome(null);

    const result = await setSupplierConfirmedDate(order.po_id, lineNo, date, {
      apiBaseUrl,
      fetchImpl,
    });

    setPending(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      // คงค่าที่พิมพ์ไว้ในช่อง — draft ไม่ถูกล้าง
      setConfirmedDateOutcome({ poId: order.po_id, lineNo, failure: result });
      return;
    }

    setConfirmedDateOutcome({ poId: order.po_id, lineNo, failure: null });
    setConfirmedDateDrafts((previous) => {
      const next = { ...previous };
      delete next[confirmedDateKey(order.po_id, lineNo)];
      return next;
    });
    setOrders((previous) =>
      previous.status === 'ready'
        ? {
            status: 'ready',
            items: previous.items.map((item) =>
              item.po_id === result.order.po_id ? result.order : item,
            ),
          }
        : previous,
    );
  };

  const items = orders.status === 'ready' ? orders.items : [];
  const matchedItems = filterPurchaseOrders(items, query);
  const busy = pending !== null;
  const createBody = toCreatePurchaseOrderBody(createFields, lines);
  /** ปุ่มที่เขียนได้สักปุ่มหนึ่ง — ใช้ตัดสินว่าจะขึ้นป้าย "ดูได้อย่างเดียว" ไหม */
  const canWriteAnything = canCreate || canEdit || canApprove;

  /** transition ที่แถวนี้เสนอได้จริง = เส้นทางที่มีอยู่ ∩ สิทธิ์ที่ผู้ใช้ถือ */
  const offeredActions = (order: PurchaseOrder): readonly PurchaseOrderTransition[] =>
    purchaseOrderLifecycleActionsFor(order.status).filter((transition) =>
      can(permissions, PROCUREMENT_MODULE, purchaseOrderActionPermission(transition)),
    );

  const anyRowHasActions = matchedItems.some((order) => offeredActions(order).length > 0);

  return (
    <div className="cap-page" data-testid="purchase-orders">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title">
            ใบสั่งซื้อ (Purchase Order)
            <InfoHint
              label="เกี่ยวกับหน้านี้"
              text={
                <>
                  คำผูกมัดกับผู้ขาย — เกิดจากการ<strong>รวมใบขอซื้อที่อนุมัติแล้ว</strong>ของ
                  ผู้ขายรายเดียวเข้าเป็นใบเดียว (D-085) รายการด้านล่างแสดงเฉพาะโรงงานที่คุณมี
                  สิทธิ์เข้าถึง
                </>
              }
            />
          </h1>
        </div>
      </div>

      {!canWriteAnything ? (
        <div className="cap-notice" data-testid="po-read-only" role="status">
          <StatusBadge tone="warning">ดูได้อย่างเดียว</StatusBadge>
          <h2 className="cap-notice__title">สิทธิ์ชุดนี้ยังออกใบหรือขับสถานะไม่ได้</h2>
          <p className="cap-notice__text">
            สิทธิ์ที่เซิร์ฟเวอร์ส่งมาไม่มี flag{' '}
            <code className="cap-table__code">can_create</code>,{' '}
            <code className="cap-table__code">can_edit</code> หรือ{' '}
            <code className="cap-table__code">can_approve</code> ของโมดูล{' '}
            <code className="cap-table__code">{PROCUREMENT_MODULE}</code> ปุ่ม
            &quot;ออกใบสั่งซื้อ&quot; และปุ่มในแต่ละแถวจึงไม่แสดง — รายการและช่องค้นหา
            ยังใช้ได้ตามปกติ
          </p>
          <p className="cap-notice__note">
            การซ่อนปุ่มเป็นเพียง UX เพื่อไม่ให้กดสิ่งที่จะถูกปฏิเสธ — ผู้ตัดสินจริงคือ
            backend ทุกคำขอเสมอ
          </p>
        </div>
      ) : null}

      {/* ------------------------------------------------- ค้นหา + ปุ่มสร้าง -- */}

      <div className="cap-filters" data-testid="po-toolbar">
        <label className="cap-field">
          <span className="cap-field__label">ค้นหาใบสั่งซื้อ</span>
          <input
            className="cap-input"
            type="search"
            name="po_query"
            data-testid="po-search"
            value={query}
            autoComplete="off"
            onChange={(event) => {
              setQuery(event.target.value);
            }}
          />
          <InfoHint
            label="เกี่ยวกับช่องค้นหา"
            text="กรองรายการด้านล่างจากเลขที่ใบหรือรหัสผู้ขาย — กรองจากรายการที่โหลดมาแล้ว
            ไม่ได้ถามเซิร์ฟเวอร์ใหม่ ช่องว่างหมายถึงแสดงทุกใบ"
          />
        </label>

        {canCreate ? (
          <button
            type="button"
            className="cap-button cap-button--primary"
            data-testid="po-create-open"
            onClick={openCreate}
          >
            ออกใบสั่งซื้อ
          </button>
        ) : null}
      </div>

      {/* --------------------------------------------------------- สร้างใหม่ -- */}

      <Modal
        open={canCreate && createOpen}
        onClose={closeCreate}
        title="ออกใบสั่งซื้อจากใบขอซื้อที่อนุมัติแล้ว"
      >
        <form
          className="cap-form"
          data-testid="po-create-form"
          aria-label="ออกใบสั่งซื้อใหม่"
          onSubmit={(event) => {
            event.preventDefault();
            void submitCreate();
          }}
        >
          <label className="cap-field">
            <span className="cap-field__label">เลขที่ใบสั่งซื้อ</span>
            <input
              className="cap-input"
              name="po_number"
              data-testid="po-create-number"
              value={createFields.poNumber}
              maxLength={PO_NUMBER_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => {
                updateField('poNumber', event.target.value);
              }}
            />
            <InfoHint
              label="เกี่ยวกับเลขที่ใบสั่งซื้อ"
              text={
                <>
                  ยาวไม่เกิน {PO_NUMBER_MAX_LENGTH} ตัวอักษร ต้องไม่ซ้ำกันทั้งระบบ และ{' '}
                  <strong>แก้ไม่ได้อีกเลยหลังออกใบ</strong> — ผู้ขายจะอ้างเลขนี้กลับมา
                </>
              }
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">รหัสโรงงาน</span>
            <input
              className="cap-input"
              name="plant_code"
              data-testid="po-create-plant"
              value={createFields.plantCode}
              maxLength={PLANT_CODE_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => {
                updateField('plantCode', event.target.value);
              }}
            />
            <InfoHint
              label="เกี่ยวกับรหัสโรงงาน"
              text={
                <>
                  ต้องเป็นโรงงานที่คุณมีสิทธิ์เข้าถึง และ{' '}
                  <strong>ต้องเป็นโรงงานเดียวกับใบขอซื้อทุกใบที่เลือก</strong> (D-004)
                </>
              }
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">รหัสผู้ขาย</span>
            <input
              className="cap-input"
              name="supplier_code"
              data-testid="po-create-supplier"
              value={createFields.supplierCode}
              maxLength={SUPPLIER_CODE_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => {
                updateField('supplierCode', event.target.value);
              }}
            />
            <InfoHint
              label="เกี่ยวกับรหัสผู้ขาย"
              text="บังคับกรอก — ใบสั่งซื้อที่ไม่มีผู้ขายไม่ได้ผูกมัดกับใคร ผู้ขายที่ถูกระงับ
              หรือเลิกใช้แล้วรับคำสั่งซื้อไม่ได้ (D-083)"
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">หมายเหตุ (ไม่บังคับ)</span>
            <input
              className="cap-input"
              name="note"
              data-testid="po-create-note"
              value={createFields.note}
              maxLength={PO_NOTE_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => {
                updateField('note', event.target.value);
              }}
            />
          </label>

          {/* ------------------------------------ ใบขอซื้อที่สั่งซื้อได้ -- */}

          <fieldset className="cap-field" data-testid="po-create-lines">
            <legend className="cap-field__label">
              ใบขอซื้อที่จะรวมเข้าใบนี้
              <InfoHint
                label="เกี่ยวกับตารางใบขอซื้อ"
                text={
                  <>
                    ติ๊กใบขอซื้อที่อนุมัติแล้วซึ่งจะซื้อจากผู้ขายรายนี้ แล้วใส่{' '}
                    <strong>ราคาต่อหน่วย</strong> ให้แต่ละใบ — สินค้า จำนวน และหน่วยนับถูก
                    คัดลอกจากใบขอซื้อโดยเซิร์ฟเวอร์ หน้าจอนี้จึงไม่มีช่องให้แก้ และยอดรวมของ
                    แต่ละบรรทัดจะถูกคำนวณฝั่งเซิร์ฟเวอร์หลังออกใบ
                  </>
                }
              />
            </legend>

            {orderable.status === 'loading' ? (
              <p
                className="cap-notice__text"
                data-testid="po-create-lines-loading"
                role="status"
              >
                กำลังโหลดใบขอซื้อที่อนุมัติแล้ว…
              </p>
            ) : null}

            {orderable.status !== 'loading' && orderable.status !== 'ready' ? (
              <PurchaseOrderFailureNotice
                failure={orderable}
                context="requisitions"
                onRetry={() => {
                  setOrderableToken((token) => token + 1);
                }}
              />
            ) : null}

            {orderable.status === 'ready' && orderableItems.length === 0 ? (
              <div className="cap-notice" data-testid="po-create-lines-empty" role="status">
                <StatusBadge tone="neutral">ยังไม่มีใบขอซื้อที่สั่งซื้อได้</StatusBadge>
                <p className="cap-notice__text">
                  ไม่มีใบขอซื้อที่อยู่ในสถานะ &quot;อนุมัติแล้ว&quot; ในโรงงานที่คุณเข้าถึง
                  ได้ — ใบที่ถูกสั่งซื้อไปแล้วจะไม่อยู่ในรายการนี้อีก (สั่งซื้อได้ครั้งเดียว)
                </p>
              </div>
            ) : null}

            {orderable.status === 'ready' && orderableItems.length > 0 ? (
              <div className="cap-table-wrap">
                <table className="cap-table" data-testid="po-create-lines-table">
                  <caption className="cap-visually-hidden">
                    ใบขอซื้อที่อนุมัติแล้วและยังไม่ถูกสั่งซื้อ พร้อมช่องราคาต่อหน่วย
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">เลือก</th>
                      <th scope="col">เลขที่ใบขอซื้อ</th>
                      <th scope="col">โรงงาน</th>
                      <th scope="col">สินค้า</th>
                      <th scope="col">จำนวน</th>
                      <th scope="col">ต้องการวันที่</th>
                      <th scope="col">ราคาต่อหน่วย</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderableItems.map((requisition) => {
                      const picked = requisition.pr_number in linePrices;
                      const price = linePrices[requisition.pr_number] ?? '';

                      return (
                        <tr
                          key={requisition.pr_id}
                          data-testid={`po-create-pr-${requisition.pr_number}`}
                        >
                          <td>
                            <input
                              type="checkbox"
                              name={`pick_${requisition.pr_number}`}
                              data-testid={`po-create-pick-${requisition.pr_number}`}
                              aria-label={`เลือกใบขอซื้อ ${requisition.pr_number}`}
                              checked={picked}
                              disabled={busy}
                              onChange={() => {
                                togglePick(requisition.pr_number);
                              }}
                            />
                          </td>
                          <th scope="row" className="cap-table__row-head">
                            <code className="cap-table__code">{requisition.pr_number}</code>
                          </th>
                          <td>
                            <code className="cap-table__code">{requisition.plant_code}</code>
                          </td>
                          <td>
                            <code className="cap-table__code">
                              {requisition.product_code}
                            </code>
                          </td>
                          <td>
                            {requisition.quantity} {requisition.uom_code}
                          </td>
                          <td>{requiredDateLabel(requisition.required_date)}</td>
                          <td>
                            <input
                              className="cap-input"
                              name={`unit_price_${requisition.pr_number}`}
                              data-testid={`po-create-price-${requisition.pr_number}`}
                              aria-label={`ราคาต่อหน่วยของใบขอซื้อ ${requisition.pr_number}`}
                              value={price}
                              inputMode="decimal"
                              autoComplete="off"
                              disabled={!picked || busy}
                              onChange={(event) => {
                                setPrice(requisition.pr_number, event.target.value);
                              }}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}
          </fieldset>

          <div className="cap-form__actions">
            <button
              type="submit"
              className="cap-button cap-button--primary"
              data-testid="po-create-submit"
              disabled={busy || createBody === null}
            >
              {pending?.kind === 'create' ? 'กำลังออกใบ…' : 'ออกใบสั่งซื้อ'}
            </button>
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="po-create-cancel"
              disabled={busy}
              onClick={closeCreate}
            >
              ยกเลิก
            </button>
          </div>

          <p className="cap-field__hint" data-testid="po-create-summary">
            เลือกไว้ {lines.length} ใบ
            {lines.length === 0
              ? ' — ต้องเลือกอย่างน้อยหนึ่งใบ และใส่ราคาต่อหน่วยให้ครบทุกใบจึงจะออกใบได้'
              : ''}
          </p>

          {/**
           * ความล้มเหลวอยู่ **ในกล่อง** ข้างฟอร์มที่ทำให้มันเกิด — ผู้ใช้แก้แล้วกดส่ง
           * ใหม่ได้ทันทีโดยไม่ต้องเปิดกล่องใหม่และติ๊กใหม่ทั้งหมด
           */}
          {createFailure === null ? null : (
            <PurchaseOrderFailureNotice failure={createFailure} context="create" />
          )}
        </form>
      </Modal>

      {createdOrder === null ? null : (
        <div className="cap-notice" data-testid="po-create-result" role="status">
          <StatusBadge tone="success">ออกใบแล้ว</StatusBadge>
          <h2 className="cap-notice__title">
            ออกใบสั่งซื้อ {createdOrder.po_number} แล้ว
          </h2>
          <p className="cap-notice__text">
            ผู้ขาย <code className="cap-table__code">{createdOrder.supplier_code}</code>{' '}
            จำนวน {createdOrder.lines.length} บรรทัด สถานะ{' '}
            {purchaseOrderStatusLabel(createdOrder.status)} —{' '}
            <strong>ใบขอซื้อที่รวมเข้ามาถูกเปลี่ยนเป็น &quot;ออกใบสั่งซื้อแล้ว&quot;</strong>{' '}
            ในธุรกรรมเดียวกัน และใบนี้ยังไม่ถูกส่งให้ผู้ขายจนกว่าจะผ่านการอนุมัติ
          </p>
          <ul data-testid="po-create-result-lines">
            {createdOrder.lines.map((line) => (
              <li className="cap-notice__text" key={line.line_no}>
                {line.pr_number} — {line.product_code} {line.quantity} {line.uom_code} ×{' '}
                {line.unit_price} = {line.line_amount}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* --------------------------------------------- ผลของปุ่มที่กดสำเร็จ -- */}

      {actionOutcome?.order == null ? null : (
        <div className="cap-notice" data-testid="po-action-result" role="status">
          <StatusBadge tone="success">บันทึกแล้ว</StatusBadge>
          <h2 className="cap-notice__title">
            ใบสั่งซื้อ {actionOutcome.order.po_number} อยู่ที่สถานะ{' '}
            {purchaseOrderStatusLabel(actionOutcome.order.status)} แล้ว
          </h2>
          <p className="cap-notice__text">
            สถานะนี้มาจากคำตอบของเซิร์ฟเวอร์ ไม่ใช่จากสิ่งที่หน้าจอเพิ่งส่งไป —
            และรายการด้านล่างถูกโหลดใหม่ทั้งหน้าเพื่อให้เห็นสิ่งที่คนอื่นเปลี่ยนไปด้วย
          </p>
        </div>
      )}

      {/* ------------------------------------------------------- รายการใบ -- */}

      {orders.status === 'loading' ? (
        <div className="cap-notice" data-testid="po-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดรายการใบสั่งซื้อ…</p>
        </div>
      ) : null}

      {orders.status !== 'loading' && orders.status !== 'ready' ? (
        <PurchaseOrderFailureNotice
          failure={orders}
          context="orders"
          onRetry={() => {
            setReloadToken((token) => token + 1);
          }}
        />
      ) : null}

      {orders.status === 'ready' && items.length === 0 ? (
        <div className="cap-notice" data-testid="po-empty" role="status">
          <StatusBadge tone="neutral">ยังไม่มีใบสั่งซื้อ</StatusBadge>
          <h2 className="cap-notice__title">
            ยังไม่มีใบสั่งซื้อในโรงงานที่คุณเข้าถึงได้
          </h2>
          <p className="cap-notice__text">
            เซิร์ฟเวอร์ตอบสำเร็จแต่รายการว่างเปล่า — ไม่ใช่การโหลดที่ล้มเหลว
          </p>
        </div>
      ) : null}

      {/**
       * "หาไม่เจอ" ไม่ใช่ "ยังไม่มีใบสั่งซื้อ" และไม่ใช่ "โหลดไม่สำเร็จ" — ทั้งสามให้
       * หน้าจอที่ไม่มีตารางเหมือนกัน แต่สิ่งที่ผู้ใช้ต้องทำต่อคนละเรื่องกันโดยสิ้นเชิง
       */}
      {orders.status === 'ready' && items.length > 0 && matchedItems.length === 0 ? (
        <div className="cap-notice" data-testid="po-no-match" role="status">
          <StatusBadge tone="neutral">ไม่พบใบที่ค้นหา</StatusBadge>
          <h2 className="cap-notice__title">
            ไม่มีใบสั่งซื้อที่ตรงกับ &quot;{query.trim()}&quot;
          </h2>
          <p className="cap-notice__text">
            รายการนี้มีใบสั่งซื้อ {items.length} ใบ แต่ไม่มีใบใดที่เลขที่ใบหรือรหัสผู้ขาย
            มีคำนี้อยู่ — ล้างคำค้นเพื่อดูทั้งหมด
          </p>
        </div>
      ) : null}

      {orders.status === 'ready' && matchedItems.length > 0 ? (
        <section className="cap-card cap-card--table" aria-label="ใบสั่งซื้อทั้งหมด">
          <div className="cap-card__head">
            <h2 className="cap-card__title">
              ใบสั่งซื้อทั้งหมด
              <InfoHint
                label="เกี่ยวกับสถานะที่ปุ่มอ้างถึง"
                text={PURCHASE_ORDER_STATUS_DESCRIPTORS.map(
                  (descriptor) => `${descriptor.label} — ${descriptor.summary}`,
                ).join(' / ')}
              />
            </h2>
            <span className="cap-card__hint">
              {query.trim() === ''
                ? 'เรียงตามเลขที่ใบสั่งซื้อตามที่เซิร์ฟเวอร์ส่งมา'
                : `แสดง ${matchedItems.length} จาก ${items.length} ใบที่ตรงกับคำค้น`}
            </span>
          </div>
          <div className="cap-table-wrap">
            <table className="cap-table" data-testid="po-table">
              <caption className="cap-visually-hidden">
                ใบสั่งซื้อของโรงงานที่คุณเข้าถึงได้ พร้อมบรรทัด สถานะ และปุ่มที่สถานะนั้น
                อนุญาต
              </caption>
              <thead>
                <tr>
                  <th scope="col">เลขที่ใบ</th>
                  <th scope="col">โรงงาน</th>
                  <th scope="col">ผู้ขาย</th>
                  <th scope="col">
                    รายการในใบ
                    <InfoHint
                      label="เกี่ยวกับวันส่งที่ Supplier ยืนยัน"
                      text={
                        <>
                          &quot;วันส่งที่ Supplier ยืนยัน&quot; ต่อบรรทัดคือวันที่ผู้ขาย
                          รับปากว่าจะส่งของจริง (ต่างจาก &quot;ต้องการวันที่&quot; ของใบ
                          ขอซื้อซึ่งเป็นวันที่ **เราต้องการ**) ฝ่ายจัดซื้อเป็นผู้กรอกหลัง
                          ผู้ขายยืนยันทางอีเมลหรือโทรศัพท์ — ค่านี้เดินทางไปกับ feed ที่
                          Store ดึงจาก ERP (D-163) ให้โรงงานวางแผนรับของได้แม่นขึ้น กรอก
                          ได้เฉพาะใบที่อยู่ที่{' '}
                          {CONFIRMED_DATE_EDITABLE_STATUSES.map((status) =>
                            purchaseOrderStatusLabel(status),
                          ).join(', ')}{' '}
                          และต้องถือสิทธิ์ EDIT ของ {PROCUREMENT_MODULE}
                        </>
                      }
                    />
                  </th>
                  <th scope="col">สถานะ</th>
                  {anyRowHasActions ? <th scope="col">การดำเนินการ</th> : null}
                </tr>
              </thead>
              <tbody>
                {matchedItems.map((order) => {
                  const actions = offeredActions(order);
                  const outcome =
                    actionOutcome?.poId === order.po_id && actionOutcome.failure !== null
                      ? actionOutcome
                      : null;

                  return (
                    <tr
                      key={order.po_id}
                      data-testid={`po-row-${order.po_number}`}
                      data-status={order.status}
                    >
                      <th scope="row" className="cap-table__row-head">
                        <code className="cap-table__code">{order.po_number}</code>
                      </th>
                      <td>
                        <code className="cap-table__code">{order.plant_code}</code>
                      </td>
                      <td>
                        <code className="cap-table__code">{order.supplier_code}</code>
                      </td>
                      <td>
                        {/**
                         * บรรทัดมากับหัวใบอยู่แล้ว (contract ส่งมาด้วยกัน) — การซ่อนไว้
                         * หลังคำขอที่สองจะทำให้ผู้ใช้ตัดสินใจ "อนุมัติ" หรือ "ยกเลิก"
                         * คำผูกมัดโดยไม่เห็นว่าข้างในมีอะไร
                         */}
                        <ul>
                          {order.lines.map((line) => {
                            const confirmedKey = confirmedDateKey(order.po_id, line.line_no);
                            const currentConfirmedDate = line.supplier_confirmed_date ?? '';
                            const confirmedDraft =
                              confirmedDateDrafts[confirmedKey] ?? currentConfirmedDate;
                            const confirmedEditable = canEditConfirmedDate(
                              order.status,
                              permissions,
                            );
                            const confirmedOutcome =
                              confirmedDateOutcome !== null &&
                              confirmedDateOutcome.poId === order.po_id &&
                              confirmedDateOutcome.lineNo === line.line_no
                                ? confirmedDateOutcome
                                : null;
                            const savingThisLine =
                              pending?.kind === 'confirmed-date' &&
                              pending.poId === order.po_id &&
                              pending.lineNo === line.line_no;
                            const testIdBase = `po-line-confirmed-date-${order.po_number}-${line.line_no}`;

                            return (
                              <li key={line.line_no}>
                                <code className="cap-table__code">{line.product_code}</code>{' '}
                                {line.quantity} {line.uom_code} × {line.unit_price} ={' '}
                                {line.line_amount}{' '}
                                <span className="cap-table__muted">({line.pr_number})</span>
                                <div className="cap-field cap-field--inline" data-testid={testIdBase}>
                                  <span className="cap-field__label">
                                    วันส่งที่ Supplier ยืนยัน
                                  </span>
                                  {confirmedEditable ? (
                                    <>
                                      <input
                                        type="date"
                                        className="cap-input"
                                        aria-label={`วันส่งที่ Supplier ยืนยันของบรรทัด ${line.pr_number}`}
                                        data-testid={`${testIdBase}-input`}
                                        value={confirmedDraft}
                                        disabled={busy}
                                        onChange={(event) => {
                                          updateConfirmedDateDraft(
                                            order.po_id,
                                            line.line_no,
                                            event.target.value,
                                          );
                                        }}
                                      />
                                      <button
                                        type="button"
                                        className="cap-button cap-button--ghost"
                                        data-testid={`${testIdBase}-save`}
                                        disabled={busy || confirmedDraft === currentConfirmedDate}
                                        onClick={() => {
                                          void submitConfirmedDate(
                                            order,
                                            line.line_no,
                                            confirmedDraft.trim() === '' ? null : confirmedDraft,
                                          );
                                        }}
                                      >
                                        {savingThisLine ? 'กำลังบันทึก…' : 'บันทึก'}
                                      </button>
                                      <button
                                        type="button"
                                        className="cap-button cap-button--ghost"
                                        data-testid={`${testIdBase}-clear`}
                                        disabled={busy}
                                        onClick={() => {
                                          updateConfirmedDateDraft(order.po_id, line.line_no, '');
                                          void submitConfirmedDate(order, line.line_no, null);
                                        }}
                                      >
                                        ล้าง
                                      </button>
                                      {confirmedOutcome !== null &&
                                      confirmedOutcome.failure === null ? (
                                        <span
                                          className="cap-notice__note"
                                          data-testid={`${testIdBase}-saved`}
                                        >
                                          บันทึกแล้ว
                                        </span>
                                      ) : null}
                                      {confirmedOutcome !== null &&
                                      confirmedOutcome.failure !== null ? (
                                        <div data-testid={`${testIdBase}-failure`}>
                                          <PurchaseOrderFailureNotice
                                            failure={confirmedOutcome.failure}
                                            context="confirmed-date"
                                          />
                                        </div>
                                      ) : null}
                                    </>
                                  ) : (
                                    <span data-testid={`${testIdBase}-value`}>
                                      {confirmedDateLabel(line.supplier_confirmed_date)}
                                    </span>
                                  )}
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </td>
                      <td>
                        <StatusBadge tone={purchaseOrderStatusTone(order.status)}>
                          {purchaseOrderStatusLabel(order.status)}
                        </StatusBadge>
                      </td>
                      {anyRowHasActions ? (
                        <td data-testid={`po-actions-${order.po_number}`}>
                          {actions.map((transition) => (
                            <button
                              key={transition}
                              type="button"
                              className="cap-button cap-button--ghost"
                              data-testid={`po-action-${transition}-${order.po_number}`}
                              disabled={busy}
                              onClick={() => {
                                void runTransition(order, transition);
                              }}
                            >
                              {pending?.kind === transition && pending.poId === order.po_id
                                ? 'กำลังส่ง…'
                                : purchaseOrderActionLabel(transition)}
                            </button>
                          ))}

                          {/**
                           * คำอธิบายอยู่ **ที่แถวที่ถูกปฏิเสธ** ไม่ใช่ลอยอยู่ท้ายหน้า —
                           * `APPROVER_MUST_DIFFER_FROM_CREATOR` คือกรณีที่ผู้ใช้ต้องเห็น
                           * ทันทีว่ามันเป็นเรื่องของ **ใบนี้** ไม่ใช่ของสิทธิ์เขา
                           */}
                          {outcome === null || outcome.failure === null ? null : (
                            <div data-testid="po-action-failure-slot">
                              <PurchaseOrderFailureNotice
                                failure={outcome.failure}
                                context={outcome.transition}
                              />
                              <p className="cap-notice__note">
                                ปุ่มที่กดคือ &quot;
                                {purchaseOrderActionLabel(outcome.transition)}&quot; ซึ่งพา
                                ใบไปที่{' '}
                                {purchaseOrderStatusLabel(
                                  purchaseOrderActionTargetStatus(outcome.transition),
                                )}
                              </p>
                            </div>
                          )}
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
}
